"use client";
import React, { useState, useEffect } from "react";
import { Typography, Container, Box, Paper, Link } from "@mui/material";
import { Plus_Jakarta_Sans, Inter } from "next/font/google";
import { useTranslation } from "react-i18next"; // ← ADD

const PlusJakartaSans = Plus_Jakarta_Sans({
  subsets: ["latin"],
  weight: ["300", "400", "500", "600", "700"],
});

const inter = Inter({
  subsets: ["latin"],
  weight: ["400", "500", "600", "700"],
});

const TableOfContents = ({ isArabic }) => {
  // ← RECEIVE isArabic as prop
  const { t } = useTranslation(); // ← ADD
  const [activeId, setActiveId] = useState("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        });
      },
      { threshold: 0.5 },
    );

    document.querySelectorAll("h1, h2, h3, h4").forEach((heading) => {
      observer.observe(heading);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        position: "fixed",
        display: { xs: "none", sm: "none", md: "none", xl: "none" },
        right: isArabic ? "auto" : { xs: "5%", sm: "10%" }, // ← RTL: flip side
        left: isArabic ? { xs: "5%", sm: "10%" } : "auto", // ← RTL: flip side
        top: { xs: "50%", sm: "66.6%" },
        transform: "translateY(-50%)",
        width: "280px",
        p: 2,
        maxHeight: "calc(80vh - 20%)",
        overflowY: "auto",
        scrollBehavior: "smooth",
        textAlign: isArabic ? "right" : "left", // ← ADD
      }}
    >
      <Typography
        variant="h6"
        gutterBottom
        sx={{ fontFamily: PlusJakartaSans.style.fontFamily, fontWeight: "700" }}
      >
        {t("articleFourContent.toc.title")} {/* ← REPLACE */}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {[
          { id: "Introduction", key: "introduction", indent: 0 },
          { id: "RecruitmentTrends", key: "recruitmentTrends", indent: 0 },
          { id: "AI-driven recruitment", key: "aiDriven", indent: 1 },
          {
            id: "Prioritizing Candidate Experience",
            key: "candidateExperience",
            indent: 1,
          },
          { id: "DEI", key: "dei", indent: 1 },
          { id: "Gen Z Factor", key: "genZ", indent: 1 },
          {
            id: "Upskilling and reskilling initiatives",
            key: "upskilling",
            indent: 1,
          },
          { id: "Returning to the office", key: "returningOffice", indent: 1 },
          { id: "Data-driven recruitment", key: "dataDriven", indent: 1 },
          { id: "Skills-based hiring", key: "skillsBased", indent: 1 },
          {
            id: "Employee referrals and internal mobility",
            key: "employeeReferrals",
            indent: 1,
          },
          { id: "Conclusion", key: "conclusion", indent: 0 },
        ].map(({ id, key, indent }) => (
          <Typography
            key={id}
            onClick={() => scrollToSection(id)}
            sx={{
              cursor: "pointer",
              pl: isArabic ? 0 : indent * 2, // ← RTL: flip indent side
              pr: isArabic ? indent * 2 : 0, // ← RTL: indent on right
              color: activeId === id ? "primary.main" : "#4B5963",
              fontFamily: inter.style.fontFamily,
              fontSize: "14px",
              "&:hover": { color: "primary.main" },
            }}
          >
            {t(`articleFourContent.toc.${key}`)} {/* ← REPLACE */}
          </Typography>
        ))}
      </Box>
    </Paper>
  );
};

const ArticleFourContent = () => {
  const { t, i18n } = useTranslation(); // ← ADD
  const isArabic = i18n.language === "ar"; // ← ADD

  return (
    <Container maxWidth="xl" sx={{ direction: isArabic ? "rtl" : "ltr" }}>
      {" "}
      {/* ← ADD direction */}
      <TableOfContents isArabic={isArabic} /> {/* ← PASS isArabic */}
      <Paper elevation={3} sx={{ my: 4, borer: "none", boxShadow: "none" }}>
        {/* Introduction */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="Introduction"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              mt: 2,
              fontFamily: PlusJakartaSans.style.fontFamily,
              fontWeight: "700",
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
            }}
          >
            {t("articleFourContent.introduction.heading")}
          </Typography>
          <Typography
            sx={{
              mt: "1%",
              mb: ".75%",
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.introduction.p1")}
          </Typography>
          <Typography
            sx={{
              mb: ".75%",
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.introduction.p2")}
          </Typography>
          <Typography
            sx={{
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.introduction.p3")}
          </Typography>
        </Box>

        {/* Top Recruitment Trends */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="RecruitmentTrends"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              mt: 4,
              fontFamily: PlusJakartaSans.style.fontFamily,
              fontWeight: "700",
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
            }}
          >
            {t("articleFourContent.recruitmentTrends.heading")}
          </Typography>

          {/* AI-driven recruitment */}
          <Typography
            id="AI-driven recruitment"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              fontFamily: PlusJakartaSans.style.fontFamily,
              fontWeight: "600",
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
            }}
          >
            {t("articleFourContent.aiDriven.heading")}
          </Typography>
          <Typography
            sx={{
              mb: ".75%",
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.aiDriven.p1")}
          </Typography>
          <Typography
            sx={{
              mb: ".75%",
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.aiDriven.p2Start")}
            <Link
              href="https://www.tidio.com/blog/ai-recruitment/"
              target="_blank"
              sx={{ color: "#9048FB", textDecoration: "none" }}
            >
              {t("articleFourContent.aiDriven.p2Link")}
            </Link>
            {t("articleFourContent.aiDriven.p2End")}
          </Typography>
          <Typography
            sx={{
              mb: ".75%",
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.aiDriven.p3")}
          </Typography>
          <Typography
            sx={{
              mb: ".75%",
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.aiDriven.p4")}
          </Typography>
          <Typography
            sx={{
              mb: ".75%",
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.aiDriven.p5")}
          </Typography>
          <Typography
            sx={{
              mb: ".75%",
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.aiDriven.p6")}
          </Typography>
        </Box>

        {/* Prioritizing Candidate Experience */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="Prioritizing Candidate Experience"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              fontFamily: PlusJakartaSans.style.fontFamily,
              fontWeight: "600",
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
            }}
          >
            {t("articleFourContent.candidateExperience.heading")}
          </Typography>
          <Typography
            sx={{
              mb: ".75%",
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.candidateExperience.p1")}
          </Typography>
          <Typography
            sx={{
              mb: ".75%",
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.candidateExperience.p2")}
          </Typography>
          <Typography
            sx={{
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.candidateExperience.p3")}
          </Typography>
        </Box>

        {/* DEI */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="DEI"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              fontFamily: PlusJakartaSans.style.fontFamily,
              fontWeight: "600",
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
            }}
          >
            {t("articleFourContent.dei.heading")}
          </Typography>
          <Typography
            sx={{
              mb: ".75%",
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.dei.p1")}
          </Typography>
          <Typography
            sx={{
              mb: ".75%",
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.dei.p2Start")}
            <Link
              href="https://www.cnbc.com/2021/04/30/diversity-equity-and-inclusion-are-important-to-workers-survey-shows.html"
              target="_blank"
              sx={{ color: "#9048FB", textDecoration: "none" }}
            >
              {t("articleFourContent.dei.p2Link")}
            </Link>
            {t("articleFourContent.dei.p2End")}
          </Typography>
          <Typography
            sx={{
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.dei.p3")}
          </Typography>
        </Box>

        {/* Gen Z */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="Gen Z Factor"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              fontFamily: PlusJakartaSans.style.fontFamily,
              fontWeight: "600",
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
            }}
          >
            {t("articleFourContent.genZ.heading")}
          </Typography>
          <Typography
            sx={{
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.genZ.p1")}
          </Typography>
        </Box>

        {/* Upskilling */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="Upskilling and reskilling initiatives"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              fontFamily: PlusJakartaSans.style.fontFamily,
              fontWeight: "600",
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
            }}
          >
            {t("articleFourContent.upskilling.heading")}
          </Typography>
          <Typography
            sx={{
              mb: ".75%",
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.upskilling.p1")}
          </Typography>
          <Typography
            sx={{
              mb: ".75%",
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.upskilling.p2")}
          </Typography>
          <Typography
            sx={{
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.upskilling.p3")}
          </Typography>
        </Box>

        {/* Returning to Office */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="Returning to the office"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              fontFamily: PlusJakartaSans.style.fontFamily,
              fontWeight: "600",
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
            }}
          >
            {t("articleFourContent.returningOffice.heading")}
          </Typography>
          <Typography
            sx={{
              mb: ".75%",
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.returningOffice.p1")}
          </Typography>
          <Typography
            sx={{
              mb: ".75%",
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.returningOffice.p2")}
          </Typography>
          <Typography
            sx={{
              mb: ".75%",
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.returningOffice.p3")}
          </Typography>
          <Typography
            sx={{
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.returningOffice.p4")}
          </Typography>
        </Box>

        {/* Data-driven */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="Data-driven recruitment"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              fontFamily: PlusJakartaSans.style.fontFamily,
              fontWeight: "600",
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
            }}
          >
            {t("articleFourContent.dataDriven.heading")}
          </Typography>
          <Typography
            sx={{
              mb: ".75%",
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.dataDriven.p1")}
          </Typography>
          <Typography
            sx={{
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.dataDriven.p2")}
          </Typography>
        </Box>

        {/* Skills-based */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="Skills-based hiring"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              fontFamily: PlusJakartaSans.style.fontFamily,
              fontWeight: "600",
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
            }}
          >
            {t("articleFourContent.skillsBased.heading")}
          </Typography>
          <Typography
            sx={{
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.skillsBased.p1")}
          </Typography>
        </Box>

        {/* Employee referrals */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="Employee referrals and internal mobility"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              fontFamily: PlusJakartaSans.style.fontFamily,
              fontWeight: "600",
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
            }}
          >
            {t("articleFourContent.employeeReferrals.heading")}
          </Typography>
          <Typography
            sx={{
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.employeeReferrals.p1Start")}
            <Link
              href="https://www.kornferry.com/insights/this-week-in-leadership/talent-crunch-future-of-work"
              target="_blank"
              sx={{ color: "#9048FB", textDecoration: "none" }}
            >
              {t("articleFourContent.employeeReferrals.p1Link")}
            </Link>
            {t("articleFourContent.employeeReferrals.p1End")}
          </Typography>
        </Box>

        {/* Conclusion */}
        <Box sx={{ mb: 4 }}>
          <Typography
            id="Conclusion"
            variant="h4"
            component="h2"
            gutterBottom
            sx={{
              mt: 4,
              fontFamily: PlusJakartaSans.style.fontFamily,
              fontWeight: "700",
              fontSize: { xs: "24px", sm: "28px", md: "34px" },
            }}
          >
            {t("articleFourContent.conclusion.heading")}
          </Typography>
          <Typography
            sx={{
              mb: ".75%",
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.conclusion.p1")}
          </Typography>
          <Typography
            sx={{
              fontFamily: inter.style.fontFamily,
              fontSize: { xs: "13px", sm: "14px", md: "15.77px" },
              color: "#4B5963",
              fontWeight: "400",
            }}
          >
            {t("articleFourContent.conclusion.p2")}
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default ArticleFourContent;
